import React, { useMemo, useState } from "react"
import { MaterialReactTable } from "material-react-table"
import { Alert, Box, Button, Chip, Typography } from "@mui/material"
import TableChartIcon from "@mui/icons-material/TableChart"
import PrintIcon from "@mui/icons-material/Print"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import GroupApplications from "../../groups/components/GroupApplications"
import { applicationsForGroup, groupFields, applicationFields, groupApplicationRows, buildGroupReportWorkbook } from "./groupReportData"

const hiddenFields = ["orgId", "description", "icon", "serial", "clearAppStatus", "createDateTime", "updatedOnDateTime"]
const humanize = value => value == null ? "—" : String(value).replace(/_/g, " ").toLowerCase().replace(/^./, char => char.toUpperCase())

export default function GroupReportTable({ groupReportData = [], loading }) {
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState("")
  const columns = useMemo(() => [
    ...groupFields.map(field => ({
      id: field.key, accessorFn: field.value, header: field.header,
      size: field.type === "number" ? 150 : 200,
      ...(field.key.includes("DateTime") ? { enableSorting: false } : {}),
      Cell: ({ cell, row }) => field.key === "assignedApps"
        ? <Button size="small" aria-label={`View applications for ${row.original.groupName}`} onClick={() => row.toggleExpanded()}>
          {cell.getValue()} applications
        </Button>
        : field.type === "boolean" ? (cell.getValue() == null ? "—" : cell.getValue() ? "Yes" : "No")
          : field.key === "deployStatus" ? <Chip size="small" variant="outlined" label={humanize(cell.getValue())} />
            : ["deployStgy", "settlementStng"].includes(field.key) ? humanize(cell.getValue()) : cell.getValue() ?? "—",
    })),
    { id: "applicationDetails", header: "Application details", size: 280,
      accessorFn: row => applicationsForGroup(row).map(app => [app.appName, app.appId, app.appVersion, app.applicationId, app.versionCode].filter(value => value != null).join(" ")).join("; "),
      Cell: ({ row }) => <Box sx={{ maxHeight: 100, overflowY: "auto" }}>
        {applicationsForGroup(row.original).map((app, index) => <Typography key={index} variant="body2" sx={{ overflowWrap: "anywhere", mb: 0.5 }}>
          {app.appId || app.appName || "Unnamed application"} · {app.appVersion ?? "Version unavailable"}
        </Typography>)}
        {!applicationsForGroup(row.original).length && "No applications assigned"}
      </Box>,
    },
  ], [])

  const exportRows = async (table, format) => {
    // Include all filtered pages, with one group row regardless of app count.
    const rows = table.getPrePaginationRowModel().rows.map(row => row.original)
    setExporting(true)
    setError("")
    try {
      if (format === "excel") {
        const buffer = await buildGroupReportWorkbook(rows).xlsx.writeBuffer()
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "group_report.xlsx")
      } else {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" })
        const sections = [["Groups", groupFields, rows], ["Group applications", applicationFields, groupApplicationRows(rows)]]
        sections.forEach(([title, fields, data], index) => {
          if (index) doc.addPage()
          doc.text(title, 30, 30)
          autoTable(doc, { head: [fields.map(field => field.header)],
            body: data.map(row => fields.map(field => String(field.value(row) ?? ""))), startY: 45,
            styles: { fontSize: 7, overflow: "linebreak", cellPadding: 3 }, headStyles: { fillColor: [18, 109, 113] } })
        })
        doc.save("group_report.pdf")
      }
    } catch {
      setError("Unable to generate the report. Please try again.")
    } finally { setExporting(false) }
  }

  return <Box>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Expand a group to see every assigned application. Excel includes a Groups sheet and a Group applications sheet. Exports include all filtered rows across pages.
    </Typography>
    <MaterialReactTable columns={columns} data={groupReportData || []}
      state={{ isLoading: loading }}
      initialState={{ pagination: { pageSize: 10, pageIndex: 0 }, density: "compact",
        columnVisibility: Object.fromEntries(hiddenFields.map(key => [key, false])) }}
      enableColumnPinning
      muiTableBodyCellProps={{ sx: { overflowWrap: "anywhere" } }}
      renderDetailPanel={({ row }) => <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
          {hiddenFields.map(key => { const field = groupFields.find(field => field.key === key); const value = field.value(row.original)
            return <Box key={key}><Typography variant="caption" color="text.secondary">{field.header}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{value == null ? "—" : typeof value === "boolean" ? value ? "Yes" : "No" : value}</Typography></Box>
          })}
        </Box>
        <GroupApplications applications={applicationsForGroup(row.original)} />
      </Box>}
      renderTopToolbarCustomActions={({ table }) => <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 1 }}>
        <Button variant="contained" color="success" startIcon={<TableChartIcon />}
          disabled={loading || exporting || !table.getPrePaginationRowModel().rows.length} onClick={() => exportRows(table, "excel")}>
          {exporting ? "Exporting…" : "Export Excel"}
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} disabled={loading || exporting || !table.getPrePaginationRowModel().rows.length}
          onClick={() => exportRows(table, "pdf")}>PDF</Button>
      </Box>}
      renderEmptyRowsFallback={() => <Box sx={{ p: 4, textAlign: "center" }}>No groups to display. Select an organization and search.</Box>}
    />
  </Box>
}
